import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, PackageCheck, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface BulkDeliveryScannerProps {
  onDeliveryComplete?: () => void;
}

const STATUS_OPTIONS = [
  { value: "waiting_in_store", label: "מחכה בחנות", color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "completed", label: "הושלם", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "in_repair", label: "בתיקון", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "cancelled", label: "בוטל", color: "bg-red-100 text-red-800 border-red-300" },
];

export const BulkDeliveryScanner = ({ onDeliveryComplete }: BulkDeliveryScannerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scannedValue, setScannedValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastDeliveredNumber, setLastDeliveredNumber] = useState("");
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("waiting_in_store");
  
  const inputRef = useRef<HTMLInputElement>(null);
  const bufferRef = useRef<string>("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Focus input when dialog opens and after status change
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Refocus after success/error animation or status change
  useEffect(() => {
    if (!showSuccess && !showError && !isProcessing && isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [showSuccess, showError, isProcessing, isOpen, selectedStatus]);

  // Play success sound using Web Audio API
  const playSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + startTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + startTime + duration);
        
        oscillator.start(audioContext.currentTime + startTime);
        oscillator.stop(audioContext.currentTime + startTime + duration);
      };
      
      // Two ascending tones for success
      playTone(523.25, 0, 0.15);
      playTone(659.25, 0.1, 0.2);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Play error sound
  const playErrorSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 200;
      oscillator.type = 'square';
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const sendSmsNotification = async (orderId: string, orderNumber: number, newStatus: string, title: string, phone: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-repair-sms', {
        body: {
          orderId,
          orderNumber,
          newStatus,
          title,
          phone
        }
      });

      if (error) {
        console.error("Error sending SMS:", error);
      } else if (data?.success) {
        console.log("SMS sent successfully");
      }
    } catch (err) {
      console.error("Failed to send SMS notification:", err);
    }
  };

  const processDelivery = async (orderNumber: string) => {
    setIsProcessing(true);
    
    try {
      // Find the order by order number
      const { data: orders, error: findError } = await supabase
        .from("repair_orders")
        .select("*")
        .eq("order_number", parseInt(orderNumber));

      if (findError) throw findError;

      if (!orders || orders.length === 0) {
        setErrorMessage(`הזמנה #${orderNumber} לא נמצאה`);
        setShowError(true);
        playErrorSound();
        
        setTimeout(() => {
          setShowError(false);
          setErrorMessage("");
          bufferRef.current = "";
          setScannedValue("");
        }, 2000);
        return;
      }

      const order = orders[0];

      // Check if already in the selected status
      if (order.status === selectedStatus) {
        const statusLabel = STATUS_OPTIONS.find(s => s.value === selectedStatus)?.label || selectedStatus;
        setErrorMessage(`הזמנה #${orderNumber} כבר בסטטוס ${statusLabel}`);
        setShowError(true);
        playErrorSound();
        
        setTimeout(() => {
          setShowError(false);
          setErrorMessage("");
          bufferRef.current = "";
          setScannedValue("");
        }, 2000);
        return;
      }

      // Update status to selected status
      const { error: updateError } = await supabase
        .from("repair_orders")
        .update({ status: selectedStatus as any })
        .eq("id", order.id);

      if (updateError) throw updateError;

      // Get phone number for SMS
      let phoneToSend = order.guest_phone;
      if (!phoneToSend && order.user_id) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("phone")
          .eq("id", order.user_id)
          .maybeSingle();
        
        phoneToSend = profile?.phone;
      }

      // Send SMS notification
      if (phoneToSend) {
        await sendSmsNotification(order.id, order.order_number, selectedStatus, order.title, phoneToSend);
      }

      // Show success
      setLastDeliveredNumber(orderNumber);
      setDeliveryCount(prev => prev + 1);
      setShowSuccess(true);
      playSuccessSound();

      // Reset after animation
      setTimeout(() => {
        setShowSuccess(false);
        bufferRef.current = "";
        setScannedValue("");
        onDeliveryComplete?.();
      }, 1500);

    } catch (error) {
      console.error("Error processing delivery:", error);
      setErrorMessage("שגיאה בעדכון ההזמנה");
      setShowError(true);
      playErrorSound();
      
      setTimeout(() => {
        setShowError(false);
        setErrorMessage("");
        bufferRef.current = "";
        setScannedValue("");
      }, 2000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle input change - use onChange only to prevent duplicate characters
  // NOTE: On some Android WebViews / scanners there can be a small delay between digits,
  // so we wait a bit longer and only auto-process once we have at least 4 digits.
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isProcessing || showSuccess || showError) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const value = e.target.value.replace(/\D/g, "");
    bufferRef.current = value;
    setScannedValue(value);

    timeoutRef.current = setTimeout(() => {
      const buffered = bufferRef.current;
      // Prevent premature processing when only the first digit arrived
      if (buffered.length >= 4) {
        processDelivery(buffered);
      }
    }, 350);
  };

  // Handle Enter/Tab key separately (many scanners send a suffix key)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isProcessing || showSuccess || showError) return;

    if ((e.key === "Enter" || e.key === "Tab") && bufferRef.current.length > 0) {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      processDelivery(bufferRef.current);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setScannedValue("");
    setShowSuccess(false);
    setShowError(false);
    setErrorMessage("");
    bufferRef.current = "";
    if (deliveryCount > 0) {
      const statusLabel = STATUS_OPTIONS.find(s => s.value === selectedStatus)?.label || selectedStatus;
      toast.success(`${deliveryCount} הזמנות עודכנו לסטטוס ${statusLabel}`);
      setDeliveryCount(0);
    }
  };

  const handleStatusClick = (value: string) => {
    setSelectedStatus(value);
    // Immediately refocus the hidden input without any delay
    inputRef.current?.focus();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      } else {
        setIsOpen(true);
        setDeliveryCount(0);
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PackageCheck className="h-4 w-4 ml-2" />
          מסירות מרובות
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center justify-between">
            <span>עדכון הזמנות מרובות</span>
            {deliveryCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                עודכנו: {deliveryCount}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Status Selection - Radio style buttons without focus issues */}
        <div className="space-y-2 mb-4">
          <span className="text-sm font-medium">עדכן לסטטוס:</span>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleStatusClick(option.value)}
                className={cn(
                  "px-3 py-2 rounded-md border-2 text-sm font-medium transition-all",
                  selectedStatus === option.value
                    ? option.color + " ring-2 ring-offset-1 ring-primary"
                    : "bg-muted/50 text-muted-foreground border-transparent hover:bg-muted"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="py-8">
          {/* Hidden input for capturing barcode scanner input */}
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="fixed top-0 left-0 w-1 h-1 opacity-0 pointer-events-auto z-50"
            style={{ fontSize: '16px' }}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            value={scannedValue}
            autoFocus
          />

          {isProcessing ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-16 w-16 animate-spin text-primary" />
              <p className="text-muted-foreground">מעדכן הזמנה #{scannedValue}...</p>
            </div>
          ) : showSuccess ? (
            <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
              <div className="success-checkmark">
                <div className="check-icon">
                  <span className="icon-line line-tip"></span>
                  <span className="icon-line line-long"></span>
                  <div className="icon-circle"></div>
                  <div className="icon-fix"></div>
                </div>
              </div>
              <div className="text-2xl font-bold text-primary animate-in zoom-in duration-300">
                #{lastDeliveredNumber}
              </div>
              <p className="text-muted-foreground">עודכן ל{STATUS_OPTIONS.find(s => s.value === selectedStatus)?.label}</p>
            </div>
          ) : showError ? (
            <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
              <div className="error-icon">
                <X className="h-16 w-16 text-destructive" />
              </div>
              <p className="text-destructive font-medium">{errorMessage}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {/* Scanner Animation */}
              <div 
                className="ocrloader cursor-pointer" 
                onClick={() => inputRef.current?.focus()}
                role="button"
                tabIndex={0}
              >
                <em></em>
                <div>
                  <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                  <i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                  <i></i><i></i><i></i>
                </div>
                <span></span>
              </div>
              
              <p className={cn(
                "mt-6 text-sm text-muted-foreground text-center transition-opacity"
              )}>
                {scannedValue ? (
                  <span className="font-mono text-lg text-foreground">{scannedValue}</span>
                ) : (
                  "סרוק ברקוד לעדכון מסירה..."
                )}
              </p>
            </div>
          )}
        </div>

        <style>{`
          .ocrloader {
            width: 94px;
            height: 77px;
            position: relative;
            backface-visibility: hidden;
            margin: 0 auto;
          }
          
          .ocrloader span {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 20px;
            background-color: hsl(var(--primary) / 0.5);
            z-index: 1;
            transform: translateY(135%);
            animation: barcode-move 0.7s cubic-bezier(0.15, 0.44, 0.76, 0.64) infinite;
          }
          
          .ocrloader > div {
            z-index: 1;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 48%;
            backface-visibility: hidden;
          }
          
          .ocrloader i {
            display: block;
            height: 1px;
            background: hsl(var(--foreground));
            margin: 0 auto 2.2px;
            backface-visibility: hidden;
          }
          
          .ocrloader i:nth-child(2) { width: 75%; }
          .ocrloader i:nth-child(3) { width: 81%; }
          .ocrloader i:nth-child(4) { width: 87%; }
          .ocrloader i:nth-child(6) { width: 71%; }
          .ocrloader i:nth-child(7) { width: 81%; }
          .ocrloader i:nth-child(8) { width: 65%; }
          .ocrloader i:nth-child(9) { width: 83%; }
          .ocrloader i:nth-child(10) { width: 75%; }
          .ocrloader i:nth-child(12) { width: 86%; }
          .ocrloader i:nth-child(14) { width: 65%; }
          .ocrloader i:nth-child(16) { width: 75%; }
          .ocrloader i:nth-child(18) { width: 83%; }
          
          .ocrloader:before,
          .ocrloader:after,
          .ocrloader em:after,
          .ocrloader em:before {
            border-color: hsl(var(--foreground));
            content: "";
            position: absolute;
            width: 19px;
            height: 16px;
            border-style: solid;
            border-width: 0px;
          }
          
          .ocrloader:before {
            left: 0;
            top: 0;
            border-left-width: 2px;
            border-top-width: 2px;
          }
          
          .ocrloader:after {
            right: 0;
            top: 0;
            border-right-width: 2px;
            border-top-width: 2px;
          }
          
          .ocrloader em:before {
            left: 0;
            bottom: 0;
            border-left-width: 2px;
            border-bottom-width: 2px;
          }
          
          .ocrloader em:after {
            right: 0;
            bottom: 0;
            border-right-width: 2px;
            border-bottom-width: 2px;
          }
          
          @keyframes barcode-move {
            0%, 100% { transform: translateY(135%); }
            50% { transform: translateY(0%); }
            75% { transform: translateY(272%); }
          }

          /* Success Checkmark Animation */
          .success-checkmark {
            width: 80px;
            height: 80px;
            margin: 0 auto;
          }
          
          .check-icon {
            width: 80px;
            height: 80px;
            position: relative;
            border-radius: 50%;
            box-sizing: content-box;
            border: 4px solid hsl(var(--primary));
          }
          
          .check-icon::before {
            top: 3px;
            left: -2px;
            width: 30px;
            transform-origin: 100% 50%;
            border-radius: 100px 0 0 100px;
            content: '';
            height: 100px;
            position: absolute;
            background: hsl(var(--background));
            transform: rotate(-45deg);
          }
          
          .check-icon::after {
            top: 0;
            left: 30px;
            width: 60px;
            transform-origin: 0 50%;
            border-radius: 0 100px 100px 0;
            animation: rotate-circle 4.25s ease-in;
            content: '';
            height: 100px;
            position: absolute;
            background: hsl(var(--background));
            transform: rotate(-45deg);
          }
          
          .check-icon .icon-line {
            height: 5px;
            background-color: hsl(var(--primary));
            display: block;
            border-radius: 2px;
            position: absolute;
            z-index: 10;
          }
          
          .check-icon .icon-line.line-tip {
            top: 46px;
            left: 14px;
            width: 25px;
            transform: rotate(45deg);
            animation: icon-line-tip 0.75s;
          }
          
          .check-icon .icon-line.line-long {
            top: 38px;
            right: 8px;
            width: 47px;
            transform: rotate(-45deg);
            animation: icon-line-long 0.75s;
          }
          
          .check-icon .icon-circle {
            top: -4px;
            left: -4px;
            z-index: 10;
            width: 80px;
            height: 80px;
            border-radius: 50%;
            position: absolute;
            box-sizing: content-box;
            border: 4px solid hsl(var(--primary) / 0.5);
          }
          
          .check-icon .icon-fix {
            top: 8px;
            width: 5px;
            left: 26px;
            z-index: 1;
            height: 85px;
            position: absolute;
            transform: rotate(-45deg);
            background-color: hsl(var(--background));
          }
          
          .error-icon {
            width: 80px;
            height: 80px;
            margin: 0 auto;
            border-radius: 50%;
            border: 4px solid hsl(var(--destructive));
            display: flex;
            align-items: center;
            justify-content: center;
            animation: shake 0.5s ease-in-out;
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            20%, 60% { transform: translateX(-5px); }
            40%, 80% { transform: translateX(5px); }
          }
          
          @keyframes rotate-circle {
            0% { transform: rotate(-45deg); }
            5% { transform: rotate(-45deg); }
            12% { transform: rotate(-405deg); }
            100% { transform: rotate(-405deg); }
          }
          
          @keyframes icon-line-tip {
            0% { width: 0; left: 1px; top: 19px; }
            54% { width: 0; left: 1px; top: 19px; }
            70% { width: 50px; left: -8px; top: 37px; }
            84% { width: 17px; left: 21px; top: 48px; }
            100% { width: 25px; left: 14px; top: 45px; }
          }
          
          @keyframes icon-line-long {
            0% { width: 0; right: 46px; top: 54px; }
            65% { width: 0; right: 46px; top: 54px; }
            84% { width: 55px; right: 0px; top: 35px; }
            100% { width: 47px; right: 8px; top: 38px; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};
